import type { DealDashboardStats } from "@/types/deal";

function num(v: unknown, fallback = 0): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  }
  return fallback;
}

function int(v: unknown, fallback = 0): number {
  return Math.round(num(v, fallback));
}

function optionalNum(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = num(v, NaN);
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

export function emptyDealDashboardStats(): DealDashboardStats {
  return {
    pipeline_total: 0,
    pipeline_weighted: 0,
    won_count: 0,
    won_total: 0,
    lost_count: 0,
    lost_total: 0,
    avg_ticket: 0,
    avg_days_to_close_won: null,
    avg_days_to_close_lost: null,
    avg_days_to_close_all: null,
    lead_to_deal_rate: 0,
    by_stage: [],
    by_member: [],
    forecast_by_month: [],
  };
}

/** Converte o jsonb retornado por get_deals_dashboard_stats no formato DealDashboardStats. */
export function parseDealsDashboardStats(raw: unknown): DealDashboardStats {
  if (!raw || typeof raw !== "object") {
    return emptyDealDashboardStats();
  }
  const o = raw as Record<string, unknown>;

  const byStageRaw = Array.isArray(o.by_stage) ? o.by_stage : [];
  const by_stage = byStageRaw.map((row) => {
    const r = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    return {
      name: str(r.name, "—"),
      color: str(r.color, "bg-muted"),
      order_index: int(r.order_index, 0),
      deal_count: int(r.deal_count, 0),
      total_amount: num(r.total_amount, 0),
      weighted_total: num(r.weighted_total, 0),
    };
  });

  const byMemberRaw = Array.isArray(o.by_member) ? o.by_member : [];
  const by_member = byMemberRaw.map((row) => {
    const r = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    return {
      member_name: str(r.member_name, "—"),
      deal_count: int(r.deal_count, 0),
      total_amount: num(r.total_amount, 0),
      weighted_total: num(r.weighted_total, 0),
    };
  });

  const forecastRaw = Array.isArray(o.forecast_by_month) ? o.forecast_by_month : [];
  const forecast_by_month = forecastRaw.map((row) => {
    const r = row && typeof row === "object" ? (row as Record<string, unknown>) : {};
    return {
      month: str(r.month, ""),
      deal_count: int(r.deal_count, 0),
      total_amount: num(r.total_amount, 0),
      weighted_total: num(r.weighted_total, 0),
    };
  });

  return {
    pipeline_total: num(o.pipeline_total, 0),
    pipeline_weighted: num(o.pipeline_weighted, 0),
    won_count: int(o.won_count, 0),
    won_total: num(o.won_total, 0),
    lost_count: int(o.lost_count, 0),
    lost_total: num(o.lost_total, 0),
    avg_ticket: num(o.avg_ticket, 0),
    avg_days_to_close_won: optionalNum(o.avg_days_to_close_won),
    avg_days_to_close_lost: optionalNum(o.avg_days_to_close_lost),
    avg_days_to_close_all: optionalNum(o.avg_days_to_close_all),
    lead_to_deal_rate: num(o.lead_to_deal_rate, 0),
    by_stage,
    by_member,
    forecast_by_month,
  };
}
